// FEATURE INVITES
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSlate } from 'slate-react';
import { Transforms } from 'slate';
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatUnderlined,
  MdCode,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdLooksOne,
  MdLooksTwo,
  MdFormatQuote,
  MdFormatAlignLeft,
  MdFormatAlignCenter,
  MdFormatAlignRight,
  MdFormatAlignJustify,
  MdInsertLink,
  MdAttachFile,
  MdOutlineAssignment
} from 'react-icons/md';
import {
  isBlockActive,
  isMarkActive,
  isLinkActive,
  toggleBlock,
  toggleMark,
  unwrapLink,
  wrapLink,
  setMarkValue,
  getMarkValue
} from '../text-editor.helper';
import { RiBracesFill } from 'react-icons/ri';
import { ClipLoader } from '@lua-frontend/numu-ui';

const ICON_SIZE = 18;
const BulletsIcon = () => <MdFormatListBulleted size={ICON_SIZE} />;
const BoldIcon = () => <MdFormatBold size={ICON_SIZE} />;
const ItalicIcon = () => <MdFormatItalic size={ICON_SIZE} />;
const UnderlineIcon = () => <MdFormatUnderlined size={ICON_SIZE} />;
const LinkIcon = () => <MdInsertLink size={ICON_SIZE} />;
const CodeIcon = () => <MdCode size={ICON_SIZE} />;
const H1Icon = () => <MdLooksOne size={ICON_SIZE} />;
const H2Icon = () => <MdLooksTwo size={ICON_SIZE} />;
const QuoteIcon = () => <MdFormatQuote size={ICON_SIZE} />;
const NumberedListIcon = () => <MdFormatListNumbered size={ICON_SIZE} />;
const AlignLeftIcon = () => <MdFormatAlignLeft size={ICON_SIZE} />;
const AlignCenterIcon = () => <MdFormatAlignCenter size={ICON_SIZE} />;
const AlignRightIcon = () => <MdFormatAlignRight size={ICON_SIZE} />;
const AlignJustifyIcon = () => <MdFormatAlignJustify size={ICON_SIZE} />;
const VariablesIcon = () => <RiBracesFill size={ICON_SIZE} />;
const AttachmentsIcon = () => <MdAttachFile size={ICON_SIZE} />;
const AssignmentIcon = () => <MdOutlineAssignment size={ICON_SIZE} />;

const ACTIVE_COLOR = 'var(--blue-50, #0095FF)';
const INACTIVE_COLOR = 'var(--grey-60, #61656B)';

const ToolbarDivider = () => (
  <div
    style={{
      width: 1,
      height: 28,
      background: '#F2F2F3',
      flexShrink: 0
    }}
    aria-hidden
  />
);

const TOOLBAR_BUTTON_DEFS = [
  {
    itemKey: 'bullets',
    Icon: BulletsIcon,
    getActive: (ed) => isBlockActive(ed, 'bulleted-list', 'type'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'bulleted-list')
  },
  {
    itemKey: 'numbered-list',
    Icon: NumberedListIcon,
    getActive: (ed) => isBlockActive(ed, 'numbered-list', 'type'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'numbered-list')
  },
  {
    itemKey: 'bold',
    Icon: BoldIcon,
    getActive: (ed) => isMarkActive(ed, 'bold'),
    onMouseDown: (ed) => () => toggleMark(ed, 'bold')
  },
  {
    itemKey: 'italic',
    Icon: ItalicIcon,
    getActive: (ed) => isMarkActive(ed, 'italic'),
    onMouseDown: (ed) => () => toggleMark(ed, 'italic')
  },
  {
    itemKey: 'underline',
    Icon: UnderlineIcon,
    getActive: (ed) => isMarkActive(ed, 'underline'),
    onMouseDown: (ed) => () => toggleMark(ed, 'underline')
  },
  // Special controls rendered as select/input in MainToolbar loop.
  // Keep them in defs so toolbarItems.includes(itemKey) can pick them up.
  {
    itemKey: 'font-size',
    Icon: null,
    getActive: () => false,
    onMouseDown: () => () => {}
  },
  {
    itemKey: 'color',
    Icon: null,
    getActive: () => false,
    onMouseDown: () => () => {}
  },
  {
    itemKey: 'code',
    Icon: CodeIcon,
    getActive: (ed) => isMarkActive(ed, 'code'),
    onMouseDown: (ed) => () => toggleMark(ed, 'code')
  },
  {
    itemKey: 'heading-one',
    Icon: H1Icon,
    getActive: (ed) => isBlockActive(ed, 'heading-one', 'type'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'heading-one')
  },
  {
    itemKey: 'heading-two',
    Icon: H2Icon,
    getActive: (ed) => isBlockActive(ed, 'heading-two', 'type'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'heading-two')
  },
  {
    itemKey: 'block-quote',
    Icon: QuoteIcon,
    getActive: (ed) => isBlockActive(ed, 'block-quote', 'type'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'block-quote')
  },
  {
    itemKey: 'left',
    Icon: AlignLeftIcon,
    getActive: (ed) => isBlockActive(ed, 'left', 'align'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'left')
  },
  {
    itemKey: 'center',
    Icon: AlignCenterIcon,
    getActive: (ed) => isBlockActive(ed, 'center', 'align'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'center')
  },
  {
    itemKey: 'right',
    Icon: AlignRightIcon,
    getActive: (ed) => isBlockActive(ed, 'right', 'align'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'right')
  },
  {
    itemKey: 'justify',
    Icon: AlignJustifyIcon,
    getActive: (ed) => isBlockActive(ed, 'justify', 'align'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'justify')
  },
  {
    itemKey: 'link',
    Icon: LinkIcon,
    getActive: (ed) => isLinkActive(ed),
    onMouseDown: (ed) => (onOpenLinkModal) => () => {
      if (isLinkActive(ed)) unwrapLink(ed);
      else if (onOpenLinkModal) onOpenLinkModal();
      else {
        const url = window.prompt('Enter link URL');
        if (url) wrapLink(ed, url);
      }
    }
  },
  {
    itemKey: 'assignment',
    Icon: AssignmentIcon,
    getActive: (ed) => isBlockActive(ed, 'assignment', 'type'),
    onMouseDown: (ed) => () => toggleBlock(ed, 'assignment')
  }
];

const FONT_SIZES = [
  { value: '', label: 'Size' },
  { value: '12px', label: '12' },
  { value: '14px', label: '14' },
  { value: '16px', label: '16' },
  { value: '18px', label: '18' },
  { value: '20px', label: '20' },
  { value: '24px', label: '24' }
];

export const ToolbarFormatButton = ({ active, onClick, children, buttonClassName }) => {
  const applyHoverBg = (e, isActive) => {
    if (!isActive) e.currentTarget.style.backgroundColor = 'var(--grey-10, #E4E5E7)';
  };
  const resetHoverBg = (e, isActive) => {
    e.currentTarget.style.backgroundColor = isActive ? 'var(--blue-5, #E6F4FF)' : 'transparent';
  };
  const color = active ? ACTIVE_COLOR : INACTIVE_COLOR;
  return (
    <button
      type="button"
      className={buttonClassName}
      onMouseDown={(e) => {
        e.preventDefault();
        onClick?.();
      }}
      onMouseEnter={(e) => applyHoverBg(e, active)}
      onMouseLeave={(e) => resetHoverBg(e, active)}
      style={{ ...(active ? { backgroundColor: 'var(--blue-5, #E6F4FF)' } : {}), color }}
    >
      {children}
    </button>
  );
};

const VariablesButton = ({ buttonClassName, variableOptions, onVariableInsert }) => {
  const editor = useSlate();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, maxHeight: 280 });
  const wrapperRef = useRef(null);
  const options = variableOptions?.length ? variableOptions : [];

  const updateMenuPosition = useCallback(() => {
    if (!wrapperRef.current || typeof window === 'undefined') return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const GAP = 6;
    const MENU_WIDTH = 220;
    const MENU_MAX_HEIGHT = 280;
    const MIN_EDGE = 8;
    const estimatedHeight = Math.min(MENU_MAX_HEIGHT, Math.max(96, options.length * 35 + 12));
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceBelow = viewportHeight - rect.bottom - MIN_EDGE;
    const spaceAbove = rect.top - MIN_EDGE;
    const openBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;

    const top = openBelow
      ? rect.bottom + GAP
      : Math.max(MIN_EDGE, rect.top - estimatedHeight - GAP);

    const idealLeft = rect.right - MENU_WIDTH;
    const left = Math.min(
      Math.max(MIN_EDGE, idealLeft),
      Math.max(MIN_EDGE, viewportWidth - MENU_WIDTH - MIN_EDGE)
    );

    setMenuPosition({
      top,
      left,
      maxHeight: Math.max(120, openBelow ? spaceBelow : spaceAbove)
    });
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onReposition = () => updateMenuPosition();
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  const handleInsertVariable = (option) => {
    const node = {
      type: 'variable',
      key: option.key,
      label: option.label,
      children: [{ text: '' }]
    };
    Transforms.insertNodes(editor, node);
    if (onVariableInsert) {
      onVariableInsert(option);
    }
    setOpen(false);
  };

  const toggleOpen = () => {
    if (!open && wrapperRef.current) {
      updateMenuPosition();
    }
    setOpen((prev) => !prev);
  };

  const menuEl =
    open && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <div
            className="text-editor__variable-menu text-editor__variable-menu--portal"
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              minWidth: 220,
              maxHeight: menuPosition.maxHeight,
              overflowY: 'auto'
            }}
          >
            {options.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className="text-editor__variable-menu-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleInsertVariable(opt);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={wrapperRef} className="text-editor__variable-button-wrapper">
      <ToolbarFormatButton
        itemKey="variables"
        active={false}
        onClick={toggleOpen}
        buttonClassName={buttonClassName}
      >
        <VariablesIcon />
      </ToolbarFormatButton>
      {menuEl}
    </div>
  );
};

const AttachmentsButton = ({ buttonClassName, uploadAttachments }) => {
  const fileInputRef = useRef(null);

  const onAttachmentButtonClick = () => {
    if (!uploadAttachments?.isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={uploadAttachments?.onSelectFile}
      />
      <ToolbarFormatButton
        active={false}
        onClick={onAttachmentButtonClick}
        buttonClassName={buttonClassName}
      >
        {uploadAttachments?.isUploading ? (
          <ClipLoader cssOverride={{ borderWidth: 1 }} size={14} />
        ) : (
          <AttachmentsIcon />
        )}
      </ToolbarFormatButton>
    </>
  );
};

const ICON_SIZE_PX = 20;

export const MainToolbar = ({ toolbar, buttonClassName, onOpenLinkModal, uploadAttachments }) => {
  const editor = useSlate();
  const elements = [];

  const showAssignment = toolbar?.showAssignment;
  const onToggleAssignment = toolbar?.onToggleAssignment;

  const toolbarItems = toolbar?.items;
  if (!toolbarItems?.length) return null;

  TOOLBAR_BUTTON_DEFS.forEach(({ itemKey, Icon, getActive, onMouseDown }, index) => {
    if (!toolbarItems?.includes(itemKey)) return;

    // Add vertical divider before bold (after bullets)
    if (itemKey === 'bold' && toolbarItems?.includes('bullets')) {
      elements.push(<ToolbarDivider key="div-before-bold" />);
    }

    // Add vertical divider before link (after underline)
    if (itemKey === 'link' && toolbarItems?.includes('underline')) {
      elements.push(<ToolbarDivider key="div-before-link" />);
    }

    if (itemKey === 'font-size') {
      const value = getMarkValue(editor, 'fontSize') || '';
      elements.push(
        <select
          key={`font-size-${index}`}
          className={buttonClassName}
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            setMarkValue(editor, 'fontSize', v || null);
          }}
          style={{
            minWidth: 56,
            height: 28,
            padding: '0 6px',
            fontSize: 12,
            color: INACTIVE_COLOR,
            border: '0.5px solid var(--grey-20, #c9cbcf)',
            borderRadius: 4,
            cursor: 'pointer',
            backgroundColor: 'transparent'
          }}
        >
          {FONT_SIZES.map(({ value: v, label }) => (
            <option key={v || 'default'} value={v}>
              {label}
            </option>
          ))}
        </select>
      );

      return;
    }

    if (itemKey === 'color') {
      const value = getMarkValue(editor, 'color') || '#000000';
      elements.push(
        <div key={`color-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="color"
            value={value}
            onChange={(e) => setMarkValue(editor, 'color', e.target.value)}
            style={{
              width: 28,
              height: 28,
              padding: 0,
              border: '0.5px solid var(--grey-20, #c9cbcf)',
              borderRadius: 4,
              cursor: 'pointer',
              backgroundColor: 'transparent'
            }}
          />
        </div>
      );

      return;
    }

    const isAssignmentItem = itemKey === 'assignment';
    const isActive =
      isAssignmentItem && typeof showAssignment === 'boolean' ? showAssignment : getActive(editor);

    let clickHandler;
    if (isAssignmentItem && typeof onToggleAssignment === 'function') {
      clickHandler = () => onToggleAssignment(!isActive);
    } else if (itemKey === 'link') {
      clickHandler = onMouseDown(editor)(onOpenLinkModal);
    } else {
      clickHandler = onMouseDown(editor);
    }

    const customIcon = toolbar?.icons?.[itemKey];
    const iconContent =
      customIcon != null ? (
        typeof customIcon === 'string' ? (
          <img
            src={customIcon}
            alt=""
            width={ICON_SIZE_PX}
            height={ICON_SIZE_PX}
            style={{ display: 'block' }}
          />
        ) : React.isValidElement(customIcon) ? (
          customIcon
        ) : typeof customIcon === 'function' ? (
          React.createElement(customIcon)
        ) : null
      ) : (
        <Icon />
      );

    elements.push(
      <ToolbarFormatButton
        key={itemKey}
        buttonClassName={buttonClassName}
        // active={getActive(editor)}
        active={isActive}
        onClick={clickHandler}
      >
        {iconContent}
      </ToolbarFormatButton>
    );
  });

  return (
    <>
      {elements}
      {toolbarItems?.includes('attachments') && (
        <AttachmentsButton
          buttonClassName={buttonClassName}
          uploadAttachments={uploadAttachments}
        />
      )}
      {toolbarItems?.includes('variables') && (
        <VariablesButton
          buttonClassName={buttonClassName}
          variableOptions={toolbar?.variableOptions}
          onVariableInsert={toolbar?.onVariableInsert}
        />
      )}
    </>
  );
};
